import { createElement } from 'react';
import styled from 'styled-components';

import * as LayoutUtils from './utils';
import type { Text, LayoutPanels, BaseContainer, ButtonsContainer, Container } from './utils';
import { Buttons } from './shared/buttons';
import { txt } from '../../../../locales/i18n';

export const textLayoutPanels: LayoutPanels = {
	layoutName: txt('cms.layoutNames.text'),
	panels: [
		{
			label: txt('cms.panelNames.layout container'),
			formKey: 'container',
			type: 'baseContainerPanel',
		},

		{
			label: txt('cms.panelNames.text container'),
			formKey: 'textContainer',
			type: 'containerPanel',
		},

		{
			label: txt('cms.panelNames.title'),
			formKey: 'title',
			type: 'textPanel',
		},

		{
			label: txt('cms.panelNames.paragraph'),
			formKey: 'paragraph',
			type: 'textPanel',
		},

		{
			label: txt('cms.panelNames.button container'),
			formKey: 'buttonsContainer',
			type: 'buttonsContainerPanel',
		},
	],
};

export const textLayoutValues: TextLayoutProps = {
	container: LayoutUtils.defaultBaseContainer,

	title: {
		color: '#000000',
		text: 'Title Tekst',
		isBold: false,
		isItalic: false,
		isUnderlined: false,
		textAlign: 'center',
		fontFamily: 'Raleway',
		htmlTag: 'h2',
		animation: LayoutUtils.defaultAnimation,
	},

	paragraph: {
		color: '#000000',
		text: 'Lorem ipsum dolor sit, amet consectetur adipisicing elit. Officia ea repellat mollitia illo fugiat, ullam et aliquam nostrum veniam quam beatae.',
		isBold: false,
		isItalic: false,
		isUnderlined: false,
		textAlign: 'center',
		fontFamily: 'Raleway',
		htmlTag: 'p',
		animation: LayoutUtils.defaultAnimation,
	},

	buttonsContainer: LayoutUtils.defaultButtonsContainer,
	textContainer: LayoutUtils.defaultContainer,
};

type TextLayoutProps = {
	container: BaseContainer;
	title: Text;
	paragraph: Text;
	buttonsContainer: ButtonsContainer;
	textContainer: Container;
};

export const TextLayout = (props: TextLayoutProps) => {
	const header = createElement(props.title.htmlTag, {
		className: 'header',
		dangerouslySetInnerHTML: { __html: LayoutUtils.formatText(props.title.text) },
		...LayoutUtils.getAnimationProps(props.title.animation),
	});

	const paragraph = createElement(props.paragraph.htmlTag, {
		className: 'paragraph',
		dangerouslySetInnerHTML: { __html: LayoutUtils.formatText(props.paragraph.text) },
		...LayoutUtils.getAnimationProps(props.paragraph.animation),
	});

	return (
		<TextLayoutPreviewStyled
			styledProps={props}
			{...LayoutUtils.getAnimationProps(props.container.animation)}
		>
			<div
				className="text-container"
				{...LayoutUtils.getAnimationProps(props.textContainer.animation)}
			>
				{props.title.text !== '' && header}
				{props.paragraph.text !== '' && paragraph}
				<Buttons {...props.buttonsContainer} />
			</div>
		</TextLayoutPreviewStyled>
	);
};

const TextLayoutPreviewStyled = styled.div<{ styledProps: TextLayoutProps }>`
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	padding: 2rem;

	background-repeat: no-repeat;
	background-color: ${({ styledProps }) => styledProps.container.backgroundColor};
	background-size: ${({ styledProps }) => styledProps?.container?.coverFit ?? 'cover'};
	background-position: ${({ styledProps }) => styledProps?.container?.coverPosition ?? 'center'};
	background-image: ${({ styledProps }) => {
		if (styledProps.container.coverSource === '') return 'none';
		return `url(${LayoutUtils.getImageSource(styledProps.container.coverSource)})`;
	}};

	min-height: ${({ styledProps }) => (styledProps.container.fullscreen ? '100dvh' : 'auto')};

	.text-container {
		flex: 1;
		display: flex;
		flex-direction: column;
		justify-content: center;
		align-items: center;
		gap: 1rem;
		padding: 2rem;

		${({ styledProps }) => LayoutUtils.getContainerStyle(styledProps.textContainer)};

		.header {
			${({ styledProps }) => LayoutUtils.getTextStyle(styledProps.title)};
		}

		.paragraph {
			${({ styledProps }) => LayoutUtils.getTextStyle(styledProps.paragraph)};
		}
	}
`;
